import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, CheckCircle, XCircle, Clock, AlertTriangle, UserCheck, RefreshCw, Calendar } from 'lucide-react';
import api from 'services/api';
import { LoadingSpinner } from 'components/common';
import { formatarMoeda } from '../utils';
import { usePermissions } from 'hooks/usePermissions';
import { useAuth } from 'contexts/AuthContext';

const DetalhesLote = ({ loteId, onClose, showNotification, contasProntasParaPagar = [] }) => {
  const { hasPermission } = usePermissions();
  const canAuthorizePayment = hasPermission('administrativo.pagamento.authorize');
  const { user } = useAuth();
  
  const [lote, setLote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const [processando, setProcessando] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  const carregarLote = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/n8n/pagamento-lotes/${loteId}`);
      if (response.data.success) {
        setLote(response.data.lote);
      }
    } catch (error) {
      console.error('Erro ao carregar lote:', error);
      if (showNotification) {
        showNotification('Erro ao carregar detalhes do lote', 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [loteId, showNotification]);

  useEffect(() => {
    if (loteId) {
      carregarLote();
    }
  }, [loteId, carregarLote]);

  useEffect(() => {
    if (!loteId) return;

    const timeoutId = setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [loteId]);

  useEffect(() => {
    if (!loteId || !modalRef.current) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [loteId, onClose]);

  // Criar um mapa para busca rápida de informações das contas por ID
  // Deve ser chamado antes de qualquer retorno condicional (regra dos hooks)
  const contasMap = useMemo(() => {
    const map = {};
    contasProntasParaPagar.forEach(conta => {
      map[conta.id] = conta;
    });
    return map;
  }, [contasProntasParaPagar]);

  const handleToggleItem = (itemId) => {
    setItensSelecionados(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  // Garantir que itens seja sempre um array (proteção contra erros de serialização)
  const itens = lote && Array.isArray(lote.itens) ? lote.itens : [];

  const handleSelecionarTodos = () => {
    if (!lote || !Array.isArray(itens)) return;
    // Usar campo pode_receber_segunda_auth retornado pelo backend
    // E também verificar se o usuário atual não é o primeiro autorizador
    const itensParaAutorizar = itens
      .filter(item => {
        const podeReceberSegundaAuth = item.pode_receber_segunda_auth === true;
        const naoEhPrimeiroAutorizador = item.first_authorizer?.id !== user?.id;
        return podeReceberSegundaAuth && naoEhPrimeiroAutorizador;
      })
      .map(item => item.id);
    
    if (itensSelecionados.length === itensParaAutorizar.length) {
      setItensSelecionados([]);
    } else {
      setItensSelecionados(itensParaAutorizar);
    }
  };

  const handleAutorizarSegunda = async () => {
    if (itensSelecionados.length === 0) {
      if (showNotification) {
        showNotification('Selecione pelo menos um item para autorizar', 'warning');
      }
      return;
    }

    setProcessando(true);
    try {
      const response = await api.post(`/n8n/pagamento-lotes/${loteId}/autorizar-segunda`, {
        item_ids: itensSelecionados,
      });

      if (response.data.success) {
        if (showNotification) {
          showNotification('Segunda autorização processada. Pagamentos enviados ao N8N.', 'success');
        }
        setItensSelecionados([]);
        await carregarLote(); // Recarregar para ver status atualizado
      } else {
        if (showNotification) {
          showNotification(response.data.message || 'Erro ao processar autorização', 'error');
        }
      }
    } catch (error) {
      console.error('Erro ao autorizar:', error);
      if (showNotification) {
        showNotification(error.response?.data?.message || 'Erro ao processar autorização', 'error');
      }
    } finally {
      setProcessando(false);
    }
  };

  const handleReprocessar = async (tipo = 'todos_falhados', itemIds = null) => {
    setProcessando(true);
    try {
      const payload = { tipo };
      if (itemIds) {
        payload.item_ids = itemIds;
      }

      const response = await api.post(`/n8n/pagamento-lotes/${loteId}/reprocessar-pagamento`, payload);

      if (response.data.success) {
        if (showNotification) {
          showNotification('Reprocessamento concluído', 'success');
        }
        await carregarLote();
      } else {
        if (showNotification) {
          showNotification(response.data.message || 'Erro ao reprocessar', 'error');
        }
      }
    } catch (error) {
      console.error('Erro ao reprocessar:', error);
      if (showNotification) {
        showNotification(error.response?.data?.message || 'Erro ao reprocessar', 'error');
      }
    } finally {
      setProcessando(false);
    }
  };

  const getItemStatusInfo = (status) => {
    // Status vem de PagamentoAuthorization
    const statusMap = {
      'pending': {
        label: 'Aguardando 2ª Aut.',
        color: 'bg-[color:var(--surface-muted)] text-[color:var(--status-warn,var(--text-primary))] border border-[color:var(--status-warn,var(--border))]/40',
        icon: Clock
      },
      'authorized': {
        label: 'Autorizado',
        color: 'bg-[color:var(--surface-muted)] text-[color:var(--status-good,var(--text-primary))] border border-[color:var(--status-good,var(--border))]/40',
        icon: CheckCircle
      },
      'processed': {
        label: 'Pago',
        color: 'bg-[color:var(--surface-muted)] text-[color:var(--status-good,var(--text-primary))] border border-[color:var(--status-good,var(--border))]/40',
        icon: CheckCircle
      },
      'cancelled': {
        label: 'Falhou',
        color: 'bg-[color:var(--surface-muted)] text-[color:var(--status-bad,var(--text-primary))] border border-[color:var(--status-bad,var(--border))]/40',
        icon: XCircle
      },
    };

    return statusMap[status] || {
      label: status || 'Desconhecido',
      color: 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] border border-[color:var(--border)]',
      icon: AlertTriangle
    };
  };

  // Função getLoteStatusInfo removida - status do lote não existe mais
  // Status vem apenas de PagamentoAuthorization dos itens

  if (!loteId) {
    return null;
  }

  // Status do lote não existe mais - será calculado dinamicamente se necessário
  // Filtrar itens baseado apenas em PagamentoAuthorization
  // E também verificar se o usuário atual não é o primeiro autorizador
  const itensParaAutorizar = itens.filter(item => {
    const podeReceberSegundaAuth = item.pode_receber_segunda_auth === true;
    const naoEhPrimeiroAutorizador = item.first_authorizer?.id !== user?.id;
    return podeReceberSegundaAuth && naoEhPrimeiroAutorizador;
  });
  const itensFalhados = itens.filter(item => item.status === 'cancelled');
  const itensPagos = itens.filter(item => item.status === 'processed');
  const totalItens = itens.length;
  const totalSelecionados = itensSelecionados.length;

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 flex items-center justify-center p-2 sm:p-4 h-full">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="detalhes-lote-title"
        className="bg-[color:var(--surface)] rounded-lg shadow-xl border border-[color:var(--border)] w-full max-w-full sm:max-w-6xl max-h-[95vh] flex flex-col overflow-hidden my-auto"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-[color:var(--border)] sticky top-0 bg-[color:var(--surface)] z-10">
          <div className="flex items-start sm:items-center justify-between">
          <div className="flex-1 min-w-0 pr-2">
            <h3
              id="detalhes-lote-title"
              className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)] break-words"
            >
              {loading ? 'Carregando detalhes do lote...' : lote ? `Lote #${lote.id} ${lote.nome ? `- ${lote.nome}` : ''}` : `Lote #${loteId}`}
            </h3>
            {/* Status do lote removido - será calculado dinamicamente se necessário */}
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Fechar modal de detalhes do lote"
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] transition-colors rounded-md p-2 flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          </div>

          {!loading && lote && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a
                href="#lote-resumo"
                className="inline-flex min-h-[34px] items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 text-xs font-medium text-[color:var(--text-primary)] hover:border-[color:var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Resumo
              </a>
              <a
                href="#lote-acoes"
                className="inline-flex min-h-[34px] items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 text-xs font-medium text-[color:var(--text-primary)] hover:border-[color:var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Ações
              </a>
              <a
                href="#lote-itens"
                className="inline-flex min-h-[34px] items-center rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 text-xs font-medium text-[color:var(--text-primary)] hover:border-[color:var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
              >
                Itens ({totalItens})
              </a>
            </div>
          )}
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center p-8 sm:p-12">
            <LoadingSpinner inline size="lg" />
            <p className="mt-4 text-[color:var(--text-muted)]">Carregando dados...</p>
          </div>
        ) : !lote ? (
          <div className="flex-1 overflow-y-auto flex items-center justify-center p-8 sm:p-12">
            <p className="text-[color:var(--text-muted)]">Lote não encontrado</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Meta linha */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-[color:var(--text-muted)]">
            <div className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-2">
              <Calendar className="h-3.5 w-3.5" />
              Criado em: {lote.created_at ? new Date(lote.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-2">
              <Clock className="h-3.5 w-3.5" />
              Atualizado: {lote.updated_at ? new Date(lote.updated_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-2">
              <UserCheck className="h-3.5 w-3.5" />
              Criado por: {lote.created_by?.name || 'N/A'}
            </div>
          </div>

          {/* Resumo do lote */}
          <div id="lote-resumo" className="scroll-mt-28 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-3 sm:p-4">
              <p className="text-xs text-[color:var(--text-muted)] mb-1">Total de Itens</p>
              <p className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">{lote.total_itens}</p>
            </div>
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-3 sm:p-4">
              <p className="text-xs text-[color:var(--text-muted)] mb-1">Valor Total</p>
              <p className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">{formatarMoeda(parseFloat(lote.total_valor) || 0)}</p>
            </div>
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-3 sm:p-4">
              <p className="text-xs text-[color:var(--text-muted)] mb-1">Pagos</p>
              <p className="text-base sm:text-lg font-semibold text-[color:var(--status-good,var(--text-primary))]">{itensPagos.length}</p>
            </div>
            <div className="bg-[color:var(--surface-muted)] rounded-lg p-3 sm:p-4">
              <p className="text-xs text-[color:var(--text-muted)] mb-1">Falhados</p>
              <p className="text-base sm:text-lg font-semibold text-[color:var(--status-bad,var(--text-primary))]">{itensFalhados.length}</p>
            </div>
          </div>

          {/* Ações */}
          {canAuthorizePayment && (
            <div id="lote-acoes" className="scroll-mt-28 bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-[color:var(--text-primary)] mb-1">Segunda Autorização</h4>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    {itensParaAutorizar.length > 0
                      ? `${itensParaAutorizar.length} item(s) aguardando segunda autorização`
                      : 'Nenhum item aguardando segunda autorização'}
                  </p>
                </div>
                {itensParaAutorizar.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={handleSelecionarTodos}
                      className="min-h-[44px] px-3 py-2 text-xs font-medium text-[color:var(--text-primary)] bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                    >
                      {itensSelecionados.length === itensParaAutorizar.length ? 'Desselecionar Todos' : 'Selecionar Todos'}
                    </button>
                    <button
                      onClick={handleAutorizarSegunda}
                      disabled={processando || itensSelecionados.length === 0}
                      className="min-h-[44px] px-4 py-2 text-xs font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                    >
                      {processando ? (
                        <>
                          <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-3 w-3 mr-2" />
                          Autorizar Selecionados ({itensSelecionados.length})
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reprocessar Falhados */}
          {itensFalhados.length > 0 && canAuthorizePayment && (
            <div className="bg-[color:var(--surface-muted)] border border-[color:var(--status-bad,var(--border))]/40 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-[color:var(--status-bad,var(--text-primary))] mb-1">Itens com Falha</h4>
                  <p className="text-xs text-[color:var(--text-muted)]">
                    {itensFalhados.length} item(s) falharam no pagamento
                  </p>
                </div>
                <button
                  onClick={() => handleReprocessar('todos_falhados')}
                  disabled={processando}
                  className="min-h-[44px] px-4 py-2 text-xs font-medium text-white bg-[color:var(--status-bad,var(--accent))] rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--status-bad,var(--accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                >
                  {processando ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-2" />
                      Reprocessar Todos
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Lista de Itens */}
          <div id="lote-itens" className="scroll-mt-28">
            <h4 className="text-sm font-semibold text-[color:var(--text-primary)] mb-3">Itens do Lote</h4>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-xs text-[color:var(--text-muted)]">
              <span>Total de itens: {totalItens}</span>
              <span>Selecionados: {totalSelecionados}</span>
            </div>
            <div className="border border-[color:var(--border)] rounded-lg p-2">
              <div className="space-y-2">
              {itens.length === 0 && (
                <div className="rounded-lg border border-dashed border-[color:var(--border)] bg-[color:var(--surface-muted)] p-6 text-center">
                  <p className="text-sm text-[color:var(--text-primary)] font-medium mb-1">Este lote ainda não possui itens visíveis.</p>
                  <p className="text-xs text-[color:var(--text-muted)]">Tente atualizar o lote para sincronizar os dados mais recentes.</p>
                </div>
              )}
              {itens.map((item) => {
                const contaInfo = contasMap[item.conta_pagar_id];
                const itemStatusInfo = getItemStatusInfo(item.status);
                const ItemStatusIcon = itemStatusInfo.icon;
                const isSelecionado = itensSelecionados.includes(item.id);
                
                // Verificar se pode selecionar: deve ter pode_receber_segunda_auth E não ser o primeiro autorizador
                const podeReceberSegundaAuth = item.pode_receber_segunda_auth === true;
                const naoEhPrimeiroAutorizador = item.first_authorizer?.id !== user?.id;
                const podeSelecionar = podeReceberSegundaAuth && naoEhPrimeiroAutorizador && canAuthorizePayment;

                return (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-3 sm:p-4 ${
                      isSelecionado
                        ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]'
                        : 'border-[color:var(--border)] bg-[color:var(--surface)]'
                    } ${!podeSelecionar && podeReceberSegundaAuth ? 'opacity-75' : ''}`}
                  >
                    {/* Mensagem de bloqueio no topo do item - apenas se não pode selecionar */}
                    {!podeSelecionar && podeReceberSegundaAuth && !naoEhPrimeiroAutorizador && (
                      <div className="mb-3 pb-3 border-b border-[color:var(--border)]">
                        <div className="flex items-center gap-2 text-xs text-[color:var(--status-warn,var(--text-primary))] bg-[color:var(--surface-muted)] border border-[color:var(--status-warn,var(--border))]/40 rounded-md px-2.5 py-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-[color:var(--status-warn,var(--text-primary))]" />
                          <span className="font-medium">Você não pode autorizar este item (você fez a 1ª autorização)</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      {/* Checkbox à esquerda */}
                      {podeSelecionar && (
                        <div className="pt-1 flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={isSelecionado}
                            onChange={() => handleToggleItem(item.id)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                        </div>
                      )}
                      
                      {/* Conteúdo principal */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-mono text-xs text-[color:var(--text-muted)]">#{item.conta_pagar_id}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${itemStatusInfo.color}`}>
                            <ItemStatusIcon className="h-3 w-3 mr-1" />
                            {itemStatusInfo.label}
                          </span>
                        </div>
                        
                        {/* Informações da Conta */}
                        {contaInfo && (
                          <div className="mb-3 space-y-1">
                            <div className="text-sm font-medium text-[color:var(--text-primary)] break-words">
                              {contaInfo.fornecedor || 'Fornecedor não informado'}
                            </div>
                            <div className="text-xs text-[color:var(--text-muted)] line-clamp-2 break-words">
                              {contaInfo.descricao || contaInfo.observacao || 'Sem descrição'}
                            </div>
                            <div className="text-xs text-[color:var(--text-muted)]">
                              <span className="font-medium">Vencimento:</span> {contaInfo.vencimento || contaInfo.data_vencimento || 'N/A'}
                            </div>
                          </div>
                        )}
                        
                        {/* Autorizadores */}
                        <div className="space-y-1">
                          {item.first_authorizer && (
                            <div className="text-xs text-[color:var(--text-muted)]">
                              <span className="font-medium">1ª:</span> {item.first_authorizer.name}
                            </div>
                          )}
                          {item.second_authorizer && (
                            <div className="text-xs text-[color:var(--status-good,var(--text-primary))]">
                              <span className="font-medium">2ª:</span> {item.second_authorizer.name}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Informações à direita */}
                      <div className="text-left sm:text-right flex-shrink-0 sm:min-w-[120px]">
                        <div className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">
                          {formatarMoeda(parseFloat(item.valor) || 0)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Mensagem de erro - ocupa toda a largura */}
                    {item.error_message && (
                            <div className="mt-3 bg-[color:var(--surface-muted)] border-l-4 border-[color:var(--status-bad,var(--accent))] rounded-md p-3">
                              <div className="flex items-start">
                                <AlertTriangle className="h-4 w-4 text-[color:var(--status-bad,var(--accent))] mt-0.5 mr-2 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs text-[color:var(--text-primary)] mb-2">
                                    <div className="font-semibold mb-1">Erro no Pagamento</div>
                                    <div className="text-[color:var(--text-muted)] break-words">{item.error_message}</div>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-[color:var(--text-muted)] mt-2 pt-2 border-t border-[color:var(--border)]">
                                    <div>
                                      <span className="font-medium">Conta:</span> #{item.conta_pagar_id}
                                    </div>
                                    <div>
                                      <span className="font-medium">Horário:</span>{' '}
                                      {item.failed_at 
                                        ? new Date(item.failed_at).toLocaleString('pt-BR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })
                                        : item.updated_at 
                                          ? new Date(item.updated_at).toLocaleString('pt-BR', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })
                                          : 'N/A'}
                                    </div>
                                  </div>
                                  
                                  {/* Mostrar detalhes adicionais se disponível */}
                                  {item.payment_result?.error && (
                                    <details className="mt-2">
                                      <summary className="text-xs text-[color:var(--text-primary)] cursor-pointer hover:text-[color:var(--accent)] font-medium">
                                        Ver detalhes técnicos
                                      </summary>
                                      <div className="mt-2 text-xs bg-[color:var(--surface)] border border-[color:var(--border)] p-2 rounded overflow-x-auto">
                                        <pre className="whitespace-pre-wrap break-words">
                                          {JSON.stringify(item.payment_result, null, 2)}
                                        </pre>
                                      </div>
                                    </details>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                  </div>
                );
              })}
              </div>
            </div>
          </div>

          {/* Rodapé com ações */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-[color:var(--border)]">
            <button
              onClick={carregarLote}
              disabled={loading}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-md hover:bg-[color:var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
            <button
              onClick={onClose}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-[color:var(--accent)] rounded-md hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
            >
              Fechar
            </button>
          </div>
        </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default DetalhesLote;

